import React, { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { WheelchairSpecs } from '../types';
import { X, ChevronDown, Search } from 'lucide-react';

interface WheelchairFormProps {
  onSave: (spec: WheelchairSpecs) => void;
  onCancel: () => void;
}

const WheelchairForm: React.FC<WheelchairFormProps> = ({ onSave, onCancel }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<'details' | 'dimensions'>('details');
  const [formData, setFormData] = useState({
    type: '',
    brand: '',
    width: 60,
    length: 90,
  });
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [brandSearch, setBrandSearch] = useState('');

  // Wheelchair types
  const wheelchairTypes = [
    { key: 'manual', label: t('wheelchair.type.manual') },
    { key: 'electric', label: t('wheelchair.type.electric') },
    { key: 'sport', label: t('wheelchair.type.sport') },
    { key: 'power', label: t('wheelchair.type.power') },
    { key: 'standing', label: t('wheelchair.type.standing') },
    { key: 'custom', label: t('wheelchair.type.custom') },
  ];

  // Common wheelchair brands
  const wheelchairBrands = [
    { key: 'invacare', label: t('wheelchair.brand.invacare') },
    { key: 'quickie', label: t('wheelchair.brand.quickie') },
    { key: 'permobil', label: t('wheelchair.brand.permobil') },
    { key: 'sunrise', label: t('wheelchair.brand.sunrise') },
    { key: 'ottobock', label: t('wheelchair.brand.ottobock') },
    { key: 'pride', label: t('wheelchair.brand.pride') },
    { key: 'other', label: t('wheelchair.brand.other') },
  ];

  const filteredBrands = wheelchairBrands.filter(brand =>
    brand.label.toLowerCase().includes(brandSearch.toLowerCase())
  );

  const handleTypeSelect = (type: string) => {
    setFormData({ ...formData, type });
    setShowTypeDropdown(false);
  };

  const handleBrandSelect = (brand: string) => {
    const selectedBrand = wheelchairBrands.find(b => b.key === brand);
    setFormData({ ...formData, brand });
    setShowBrandDropdown(false);
    setBrandSearch(selectedBrand?.label || '');
  };

  const handleBrandSearchChange = (value: string) => {
    setBrandSearch(value);
    setShowBrandDropdown(true);
    // Clear the selected brand if user is typing
    if (value !== formData.brand) {
      setFormData({ ...formData, brand: '' });
    }
  };

  const handleNextStep = () => {
    if (formData.type && formData.brand) {
      setStep('dimensions');
    }
  };

  const handleSave = () => {
    const newSpec: WheelchairSpecs = {
      id: `wheelchair-${Date.now()}`,
      type: formData.type,
      brand: formData.brand,
      width: formData.width,
      length: formData.length,
      isDefault: true,
    };
    onSave(newSpec);
  };

  const getDimensionsTitle = () => {
    const typeLabel = wheelchairTypes.find(t => t.key === formData.type)?.label || formData.type;
    const brandLabel = wheelchairBrands.find(b => b.key === formData.brand)?.label || formData.brand;
    return t('onboarding.wheelchair.dimensionsTitle')
      .replace('{brand}', brandLabel)
      .replace('{type}', typeLabel);
  };

  return (
    <div className="wheelchair-form-modal">
      <div className="wheelchair-form-content">
        <header className="wheelchair-form-header">
          <h2 className="wheelchair-form-title">{t('onboarding.wheelchair.title')}</h2>
          <button
            className="close-button"
            onClick={onCancel}
            aria-label={t('aria.close')}
          >
            <X size={20} />
          </button>
        </header>

        <main className="wheelchair-form-main">
          {step === 'details' && (
            <>
              <div className="form-instruction">
                <p>{t('onboarding.wheelchair.instruction')}</p>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('onboarding.wheelchair.type')}
                </label>
                <div className="dropdown-container">
                  <button
                    className="dropdown-button"
                    onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                    aria-expanded={showTypeDropdown}
                    aria-haspopup="listbox"
                  >
                    <span>
                      {formData.type 
                        ? wheelchairTypes.find(t => t.key === formData.type)?.label 
                        : t('onboarding.wheelchair.typePlaceholder')
                      }
                    </span>
                    <ChevronDown size={16} />
                  </button>
                  
                  {showTypeDropdown && (
                    <ul className="dropdown-list" role="listbox">
                      {wheelchairTypes.map((type) => (
                        <li
                          key={type.key}
                          className="dropdown-item"
                          onClick={() => handleTypeSelect(type.key)}
                          role="option"
                          aria-selected={formData.type === type.key}
                        >
                          {type.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('onboarding.wheelchair.brand')}
                </label>
                <div className="dropdown-container">
                  <div className="search-input-container">
                    <input
                      type="text"
                      className="form-input search-input"
                      placeholder={t('onboarding.wheelchair.brandPlaceholder')}
                      value={brandSearch}
                      onChange={(e) => handleBrandSearchChange(e.target.value)}
                      onFocus={() => setShowBrandDropdown(true)}
                    />
                    <Search size={16} className="search-icon-inside" />
                  </div>
                  
                  {showBrandDropdown && (
                    <ul className="dropdown-list" role="listbox">
                      {filteredBrands.map((brand) => (
                        <li
                          key={brand.key}
                          className="dropdown-item"
                          onClick={() => handleBrandSelect(brand.key)}
                          role="option"
                          aria-selected={formData.brand === brand.key}
                        >
                          {brand.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleNextStep}
                  disabled={!formData.type || !formData.brand}
                >
                  {t('onboarding.wheelchair.nextStep')}
                </button>
              </div>
            </>
          )}

          {step === 'dimensions' && (
            <>
              <div className="form-instruction">
                <p>{getDimensionsTitle()}</p>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('onboarding.wheelchair.totalWidth')}
                </label>
                <div className="input-with-unit">
                  <input
                    type="number"
                    className="form-input"
                    value={formData.width}
                    onChange={(e) => setFormData({ ...formData, width: parseInt(e.target.value) || 0 })}
                    min="30"
                    max="120"
                  />
                  <span className="input-unit">cm</span>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('onboarding.wheelchair.totalLength')}
                </label>
                <div className="input-with-unit">
                  <input
                    type="number"
                    className="form-input"
                    value={formData.length}
                    onChange={(e) => setFormData({ ...formData, length: parseInt(e.target.value) || 0 })}
                    min="60"
                    max="150"
                  />
                  <span className="input-unit">cm</span>
                </div>
              </div>

              <div className="form-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setStep('details')}
                >
                  {t('button.back')}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  {t('onboarding.wheelchair.save')}
                </button>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default WheelchairForm;
